/**
 * General utility methods.
 */
/**
 * Extracts value of the query parameter with the specified name from the url.
 * Returns null if not found.
 *
 * @param {*} name Query parameter name
 * @param {*} url Whole url to examine
 */
import { i18n } from "i18next";
import { TFunction } from "react-i18next";

import { Mortgage, OtherMortgage } from "../model/MortgageExtensionModel";
import FrontendConfig from "./FrontEndConfig";

export function getParameterByName(name: String, url = window.location.href) {
    const escapedName = name.replace(/[[\]]/g, "\\$&");
    const regex = new RegExp("[?&]" + escapedName + "(=([^&#]*)|&|#|$)");
    const results = regex.exec(url);
    if (!results || !results[2]) return null;
    return decodeURIComponent(results[2].replace(/\+/g, " "));
}

const DEFAULT_COOKIE_NAME = "FRONT_END_CONFIG";
const DEMO_COOKIE_NAME = "DEMO_FRONT_END_CONFIG";

/**
 * Clones JavaScript object.
 */
export function copyObject(inputObject: any) {
    return JSON.parse(JSON.stringify(inputObject));
}

/**
 * Retrieves configuration for the frontend app in the form of FrontendConfig object.
 * Returns empty FrontendConfig object if no config is found.
 */
export function getFrontendConfig(): FrontendConfig {
    let config: FrontendConfig | null = getFrontendConfigCookie(DEFAULT_COOKIE_NAME);

    // try demo config instead
    if (!config) {
        config = getFrontendConfigCookie(DEMO_COOKIE_NAME);
    }
    return config ? config : new FrontendConfig();
}

function getFrontendConfigCookie(name: string): FrontendConfig | null {
    const documentCookieValue = `; ${document.cookie}`;

    const cookieKeyValuePair = documentCookieValue.split(`; ${name}=`);
    if (cookieKeyValuePair.length === 2) {
        let value = cookieKeyValuePair.pop();
        if (!value) {
            return null;
        }
        const cookie = value.split(";").shift();
        if (cookie) {
            return JSON.parse(decodeURIComponent(cookie));
        }
    }
    return null;
}

export function clearCookies() {
    clearCookie(DEFAULT_COOKIE_NAME);
    clearCookie(DEMO_COOKIE_NAME);
}

/**
 * Clears a cookie with a given name.
 */
export function clearCookie(name: string) {
    document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}

/**
 * Displays the mortgage product type in the user-friendly format.
 * If there is no translation provided, we show the type as it came from the backend, as slightly prettified string.
 */
export function renderProductType(
    mortgage: Mortgage | OtherMortgage,
    i18nObject: i18n,
    translationFunction: TFunction
) {
    if (mortgage === undefined) {
        return "";
    }
    const i18nKey = `MortgageType.${mortgage.productType}`;
    if (i18nObject.exists(i18nKey)) {
        return translationFunction(i18nKey);
    }
    return convertToPrettierStringFormat(mortgage.productType);
}

/**
 * Converts the technical name to more user-friendly one, for example "hypothek_fest_swissquote" to "Hypothek Fest Swissquote".
 * Some LUKB products have the name starting with "lukb$" in ACP, so that was also taken into account, just in case.
 */
function convertToPrettierStringFormat(inputString: string) {
    if (!inputString) {
        return "";
    }
    return inputString
        .replaceAll("$", " ")
        .replaceAll("_", " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}
